// LT-311 — Account tab content, extracted from the pre-LT-311
// SettingsPage so the hub layout can mount it at /settings/account.
// Behaviour and copy preserved verbatim; only the surrounding page shell
// and tab-dispatcher are gone (the hub layout provides both).
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { extractErrorMessage } from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { PHONE_EXAMPLE_PRETTY, PHONE_PLACEHOLDER } from '../../config/region';
import { Button, Input } from '../../ui';

interface ProfileForm {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
}

const SettingsAccountTab: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [profile, setProfile] = useState<ProfileForm>({
    email: user?.email ?? '',
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    phone: user?.phone ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  React.useEffect(() => {
    if (user) {
      setProfile({
        email: user.email ?? '',
        first_name: user.first_name ?? '',
        last_name: user.last_name ?? '',
        phone: user.phone ?? '',
      });
    }
  }, [user]);

  React.useEffect(() => {
    if (message?.type === 'success') {
      const t = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleSave = async () => {
    if (!profile.phone || !profile.phone.trim()) {
      setMessage({ type: 'error', text: 'Phone number is required' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      await updateProfile({
        first_name: profile.first_name || undefined,
        last_name: profile.last_name || undefined,
        phone: profile.phone.trim(),
      });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      track(AnalyticsEvents.SETTINGS_PROFILE_SAVED);
    } catch (err: unknown) {
      setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to update profile') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      {message ? (
        <div
          className={`rounded-ds-lg px-3 py-2 text-sm border ${
            message.type === 'success'
              ? 'bg-ok-tint border-ok-border text-ok-text'
              : 'bg-danger-tint border-danger-border text-danger-text'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      <Input
        id="settings-account-email"
        label="Email"
        value={profile.email}
        readOnly
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          id="settings-account-first-name"
          label="First name"
          value={profile.first_name}
          onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
          placeholder="First name"
        />
        <Input
          id="settings-account-last-name"
          label="Last name"
          value={profile.last_name}
          onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
          placeholder="Last name"
        />
      </div>

      <Input
        id="settings-account-phone"
        label="Mobile number"
        required
        type="tel"
        value={profile.phone}
        onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
        placeholder={PHONE_PLACEHOLDER}
        hint={`Enter a valid phone number, e.g. ${PHONE_EXAMPLE_PRETTY}`}
      />

      <div className="pt-2">
        <Button intent="primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsAccountTab;
